import React, { Component, ReactNode, ErrorInfo } from 'react';
import { Button, Result } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';

interface Props {
  children: ReactNode;
  /** Optional custom fallback UI */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary — catches any render error in the subtree and shows a
 * recovery UI instead of a blank screen.
 *
 * Wrap at the app level (App.tsx) and/or around individual lazy-loaded
 * route groups for granular recovery.
 *
 * @example
 *   <ErrorBoundary>
 *     <Routes>{allRoutes}</Routes>
 *   </ErrorBoundary>
 */
class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Only log in dev — avoid leaking stack traces to the console in production
    if (import.meta.env.DEV) {
      console.error('[ErrorBoundary] Caught render error:', error, info.componentStack);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return <>{this.props.fallback}</>;

      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            padding: '40px 24px',
          }}
        >
          <Result
            status="500"
            title="Something went wrong"
            subTitle={
              import.meta.env.DEV && this.state.error
                ? this.state.error.message
                : 'An unexpected error occurred. Please refresh the page or contact support.'
            }
            extra={
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={this.handleReset}
              >
                Try Again
              </Button>
            }
          />
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
